import { useEffect, useRef, useState, useCallback } from "react";

/**
 * useSpeech — thin wrapper around the browser Web Speech API.
 * Works in Chrome, Edge, Safari (iOS 14.5+). Returns { supported, listening, transcript, start, stop, error }.
 *
 * Options:
 *  - lang: BCP-47 locale (default "fr-FR")
 *  - continuous: keep listening until stop() (default true)
 *  - interimResults: stream partial transcripts (default true)
 *  - onFinal(text): called when a final chunk is delivered
 */
export function useSpeech({ lang = "fr-FR", continuous = true, interimResults = true, onFinal } = {}) {
  const SR =
    typeof window !== "undefined"
      ? window.SpeechRecognition || window.webkitSpeechRecognition
      : null;
  const supported = !!SR;

  const [listening, setListening] = useState(false);
  const [transcript, setTranscript] = useState("");
  const [error, setError] = useState(null);
  const recRef = useRef(null);
  const onFinalRef = useRef(onFinal);
  onFinalRef.current = onFinal;

  useEffect(() => {
    if (!SR) return;
    const rec = new SR();
    rec.lang = lang;
    rec.continuous = continuous;
    rec.interimResults = interimResults;

    rec.onresult = (event) => {
      let finalChunk = "";
      let interimChunk = "";
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const res = event.results[i];
        if (res.isFinal) finalChunk += res[0].transcript;
        else interimChunk += res[0].transcript;
      }
      if (finalChunk) {
        setTranscript((t) => (t ? t + " " : "") + finalChunk.trim());
        onFinalRef.current?.(finalChunk.trim());
      } else if (interimChunk) {
        setTranscript(interimChunk);
      }
    };

    rec.onerror = (e) => {
      setError(e.error || "speech-error");
      setListening(false);
    };
    rec.onend = () => setListening(false);

    recRef.current = rec;
    return () => {
      try { rec.stop(); } catch {}
      recRef.current = null;
    };
  }, [SR, lang, continuous, interimResults]);

  const start = useCallback(() => {
    if (!recRef.current) return;
    setError(null);
    setTranscript("");
    try {
      recRef.current.start();
      setListening(true);
    } catch (e) {
      // .start() throws if already active — safe to ignore.
    }
  }, []);

  const stop = useCallback(() => {
    try { recRef.current?.stop(); } catch {}
    setListening(false);
  }, []);

  return { supported, listening, transcript, start, stop, error };
}
